import Hue from '@assets/hue.svg';
import { IVideo } from '@models/IVideo';
import InViewVideo from '@molecules/InViewVideo/InViewVideo';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import usePreviewMode from '@utils/hooks/usePreviewMode';
import useResizeObserver from '@utils/hooks/useResizeObserver';
import useStickyOffset from '@utils/hooks/useStickyOffset';
import classNames from 'classnames';
import {
  motion,
  useMotionValueEvent,
  useScroll,
  useSpring,
  useTransform,
} from 'framer-motion';
import { useEffect, useRef, useState } from 'react';

interface Props {
  readonly cardTitleLargeText: string;
  readonly cardTitleSmallText: string;
  readonly cardSubtitle: string;
  readonly cardFootnote: string;
  readonly showCardModule: boolean;
  readonly text: string;
  readonly title: string;
  readonly video: IVideo;
}

const ExpandingVideo = ({
  cardTitleLargeText,
  cardTitleSmallText,
  cardSubtitle,
  cardFootnote,
  showCardModule,
  text,
  title,
  video,
}: Props) => {
  const scrollableRef = useRef<HTMLDivElement>(null);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const [finishedScaling, setFinishedScaling] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [mediaButtonFocusedByTab, setMediaButtonFocusedByTab] = useState(false);
  const [stickyRef, { height: viewportHeight }] = useResizeObserver();
  const [bottomDetailsRef, { height: bottomDetailsHeight }] =
    useResizeObserver();
  const isMobile = useMediaQuery(768);
  const isTablet = useMediaQuery(1440);
  const stickyOffset = useStickyOffset();
  const isPreview = usePreviewMode();
  const bottomDetailsTranslateY = isTablet
    ? -0.25 * viewportHeight
    : -0.5 * viewportHeight;
  const computedPaddingBottom = bottomDetailsHeight - -bottomDetailsTranslateY;

  const { scrollYProgress } = useScroll({
    target: scrollableRef,
    offset: ['start end', 'end end'],
  });

  const smoothScrollProgress = useSpring(scrollYProgress, {
    stiffness: 500,
    damping: 50,
    mass: 1,
  });

  const left = useTransform(
    smoothScrollProgress,
    [0, 0.4],
    [isPreview || mediaButtonFocusedByTab ? '100%' : '0%', '100%']
  );

  const getInitialScale = () => {
    if (isPreview || mediaButtonFocusedByTab) return 1;
    return isMobile ? 0.9 : 0.7;
  };

  const getStickyHeight = () => {
    if (isPreview) return 1080;
    return isTablet ? '50vh' : `calc(100vh - ${stickyOffset}px)`;
  };

  const scale = useTransform(
    smoothScrollProgress,
    [0.4, 0.8],
    [getInitialScale(), 1]
  );

  useEffect(() => {
    if (isPreview) setFinishedScaling(true);
  }, [isPreview]);

  // Detect whether we have finished scaling so start playing the video
  useMotionValueEvent(scale, 'change', (value: number) => {
    setFinishedScaling(value === 1 || mediaButtonFocusedByTab);
  });

  useMotionValueEvent(left, 'change', (value: string) => {
    const leftPercentageScrolledValue = +value.slice(0, -1);

    setVideoPlaying(isMobile || leftPercentageScrolledValue >= 10);
  });

  return (
    <section
      className="component-padding bg-black-100 text-white"
      data-testid="CT-155-ExpandingVideo"
    >
      <div
        className="relative"
        style={{ paddingBottom: computedPaddingBottom }}
      >
        <div
          className={
            isPreview || mediaButtonFocusedByTab
              ? 'h-[1080px]'
              : 'h-[200vh] lg:h-[400vh]'
          }
          ref={scrollableRef}
        >
          <motion.div
            ref={stickyRef}
            className="sticky"
            style={{
              top: stickyOffset,
              height: getStickyHeight(),
            }}
          >
            <motion.div
              style={{ scale }}
              className="h-full focus:outline-none"
              transition={{ ease: 'easeIn' }}
              ref={videoContainerRef}
              tabIndex={0}
            >
              <div className="relative h-full">
                <InViewVideo
                  hasInVideoPlayButton
                  dataTestId="CT-155-Expanding-Video-In-View-Video"
                  inVideoPlayButtonPositionClassName="top-2.5 left-2.5 md:top-5 md:left-5"
                  onFocusCallback={(e) =>
                    setMediaButtonFocusedByTab(
                      e.target.matches(':focus-visible')
                    )
                  }
                  video={video}
                  autoPlay={videoPlaying}
                  controls={false}
                  wrapperClasses="w-full h-full"
                  videoClasses="[&>div>video]:object-cover h-full"
                />
              </div>

              <motion.div
                ref={bottomDetailsRef}
                className="relative z-100 mx-5 text-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{
                  opacity: finishedScaling ? 1 : 0,
                  ...(finishedScaling && {
                    y: bottomDetailsTranslateY,
                  }),
                }}
                exit={{ opacity: 0, y: 50 }}
                transition={{ duration: 0.6, ease: 'easeInOut' }}
              >
                <div className="relative z-10 m-auto md:max-w-[600px] xl:max-w-[800px]">
                  {showCardModule && (
                    <img
                      src={`${Hue}`}
                      alt="hue"
                      className="absolute top-0 -z-10 mt-12 h-full w-full object-contain opacity-30"
                    />
                  )}
                  <h2
                    className="mb-5 lg:mb-9"
                    data-testid="CT-155-Expanding-Video-Title"
                  >
                    {title}
                  </h2>
                  <p
                    className={classNames(
                      'mb-10',
                      isMobile ? 'body-one' : 'h6'
                    )}
                    data-testid="CT-155-Expanding-Video-Text"
                  >
                    {text}
                  </p>

                  {showCardModule && (
                    <>
                      <div className="mx-2.5 mb-10 flex max-w-[636px] flex-col gap-y-[5px] rounded-[10px] bg-black-100 p-7.5 md:mx-auto lg:gap-y-5 xl:mx-auto">
                        <p
                          className="mount"
                          data-testid="CT-155-Expanding-Video-Module-Text"
                        >
                          {cardTitleLargeText}{' '}
                          <span className="h4">{cardTitleSmallText}</span>
                        </p>
                        <p
                          className={classNames(
                            'text-grey-400',
                            isMobile ? 'body-three' : 'h6'
                          )}
                          data-testid="CT-155-Expanding-Video-Module-Sub-Title"
                        >
                          {cardSubtitle}
                        </p>
                      </div>

                      <p
                        className={classNames(
                          'text-grey-400',
                          isMobile ? 'body-four' : 'body-two'
                        )}
                        data-testid="CT-155-Expanding-Video-Module-Footnote"
                      >
                        {cardFootnote}
                      </p>
                    </>
                  )}
                </div>
              </motion.div>
            </motion.div>

            <motion.div
              className="absolute bottom-0 h-1/2 w-full bg-gradient-to-b from-black/0 via-black to-black-100"
              initial={{ opacity: 0 }}
              transition={{ duration: 0.5, ease: 'easeInOut' }}
              animate={{ opacity: finishedScaling ? 1 : 0 }}
            />

            <motion.div
              className="absolute bottom-0 right-0 top-0 bg-white dark:bg-black-100"
              style={{ left: mediaButtonFocusedByTab ? '100%' : left }}
              transition={{ ease: 'easeInOut' }}
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ExpandingVideo;
